import dedicated from '$lib/assets/img/launch/dedicated.svg';
import rideshare from '$lib/assets/img/launch/rideshare.svg';
import piggyback from '$lib/assets/img/launch/piggyback.svg';
import military_government from '$lib/assets/img/launch/military_government.svg';
import { logger } from '$lib/logger';

export function getCondensedOrbitInfo(launch: import('$lib/types/getAllLaunch.js').Launch): string {
  const primaryOrbitType = humanizeOrbitType(launch.primaryOrbit.type);
  const secondaryOrbitType = launch.secondaryOrbit
    ? humanizeOrbitType(launch.secondaryOrbit.type)
    : null;

  const orbitType =
    secondaryOrbitType && launch.primaryOrbit.type !== launch.secondaryOrbit?.type
      ? `${primaryOrbitType} - ${secondaryOrbitType}`
      : primaryOrbitType;

  const primaryAltitude = launch.primaryOrbit.altitude;
  const secondaryAltitude = launch.secondaryOrbit?.altitude;

  const orbitAltitude =
    primaryAltitude && secondaryAltitude && primaryAltitude !== secondaryAltitude
      ? `${primaryAltitude} - ${secondaryAltitude} km`
      : primaryAltitude || secondaryAltitude
        ? `${primaryAltitude ?? secondaryAltitude} km`
        : null;

  return orbitAltitude ? `${orbitType} (${orbitAltitude})` : orbitType;
}

export function humanizeOrbitType(orbitType: number): string {
  switch (orbitType) {
    case 0:
      return 'LEO';
    case 1:
      return 'SSO';
    case 2:
      return 'MEO';
    case 3:
      return 'GEO';
    case 4:
      return 'GTO';
    case 5:
      return 'HEO';
    case 6:
      return 'EEO';
    case -1:
      return 'Other';
    default:
      logger.error('Out of bound orbit type');
      return 'ERROR!';
  }
}

export function humanizeLaunchType(type: number): string | null {
  switch (type) {
    case null:
      return null;
    case -1:
      return 'N/A';
    case 0:
      return 'Dedicated';
    case 1:
      return 'Rideshare';
    case 2:
      return 'Piggyback';
    case 3:
      return 'Military/Government';
    default:
      logger.error('Out of bound launch type');
      return 'ERROR!';
  }
}

export function getLaunchTypeImage(type: number): string {
  switch (type) {
    case 0:
      return dedicated;
    case 1:
      return rideshare;
    case 2:
      return piggyback;
    case 3:
      return military_government;
    default:
      throw new Error('Requested image does not exists!');
  }
}

export function getSiteName(site: import('$lib/types/getAllLaunch.js').Site | null): string {
  if (site === null) {
    return 'TBD';
  }

  return `${site.name} (${site.country})`;
}

export function getOrbitType(launch: import('$lib/types/getAllLaunch.js').Launch): string {
  const primaryOrbitType = launch.primaryOrbit.type;

  if (launch.secondaryOrbit !== null) {
    const secondaryOrbitType = launch.secondaryOrbit.type;

    if (secondaryOrbitType !== null && primaryOrbitType !== secondaryOrbitType) {
      return `${humanizeOrbitType(primaryOrbitType)} - ${humanizeOrbitType(secondaryOrbitType)}`;
    }
  }

  return humanizeOrbitType(primaryOrbitType);
}

export let launchTypes = [0, 1, 2, 3];
export let orbitTypes = [0, 1, 2, 3, 4, 5, 6, -1];

/**
 * Calculate the title for a launch filtered view given the set of configured filters
 * @param baseTitle The base title (e.g. "Launch Archive") that will be used when creating the title
 * @param filterValues A record containing the filter values. That is, not ids, but actual values (e.g. "SpaceX")
 * @returns a string containing the title for the component
 */
export function getFilteredViewTitle(
  baseTitle: string,
  filterValues: Record<FilterType, string[]>
): string {
  if (needsCustomTitle(filterValues)) {
    if (isIncluded(filterValues.operators)) {
      if (isIncluded(filterValues.sites)) {
        return `${titleToken(filterValues.operators)} from ${titleToken(filterValues.sites)} ${baseTitle}`;
      }
      if (isIncluded(filterValues.types)) {
        return `${titleToken(filterValues.operators)} ${titleToken(filterValues.types)} ${baseTitle}`;
      }
      if (isIncluded(filterValues.orbits)) {
        return `${titleToken(filterValues.operators)} to ${titleToken(filterValues.orbits)} ${baseTitle}`;
      }
      if (isIncluded(filterValues.vehicles)) {
        return `${titleToken(filterValues.operators)} with ${titleToken(filterValues.vehicles)} ${baseTitle}`;
      }
      return `${titleToken(filterValues.operators)} ${baseTitle}`;
    }
    if (isIncluded(filterValues.sites)) {
      if (isIncluded(filterValues.types)) {
        return `${titleToken(filterValues.types)} from ${titleToken(filterValues.sites)} ${baseTitle}`;
      }
      if (isIncluded(filterValues.orbits)) {
        return `${titleToken(filterValues.orbits)} from ${titleToken(filterValues.sites)} ${baseTitle}`;
      }
      if (isIncluded(filterValues.vehicles)) {
        return `${titleToken(filterValues.vehicles)} from ${titleToken(filterValues.sites)} ${baseTitle}`;
      }
      return `${titleToken(filterValues.sites)} ${baseTitle}`;
    }
    if (isIncluded(filterValues.types)) {
      if (isIncluded(filterValues.orbits)) {
        return `${titleToken(filterValues.types)} to ${titleToken(filterValues.orbits)} ${baseTitle}`;
      }
      if (isIncluded(filterValues.vehicles)) {
        return `${titleToken(filterValues.vehicles)} ${titleToken(filterValues.types)} ${baseTitle}`;
      }
      return `${titleToken(filterValues.types)} ${baseTitle}`;
    }
    if (isIncluded(filterValues.orbits)) {
      if (isIncluded(filterValues.vehicles)) {
        return `${titleToken(filterValues.vehicles)} to ${titleToken(filterValues.orbits)} ${baseTitle}`;
      }
      return `${titleToken(filterValues.orbits)} ${baseTitle}`;
    }
    if (isIncluded(filterValues.vehicles)) {
      return `${titleToken(filterValues.vehicles)} ${baseTitle}`;
    }
  }
  return baseTitle;
}

type FilterType = 'operators' | 'sites' | 'types' | 'orbits' | 'vehicles';

function needsCustomTitle(filterValues: Record<FilterType, string[]>): boolean {
  return containsFewFilters(filterValues) && containsSingleFiltersOnly(filterValues);
}

function containsSingleFiltersOnly(filterValues: Record<FilterType, string[]>): boolean {
  return (
    Object.values(filterValues).filter((values) => {
      return values.length > 1;
    }).length == 0
  );
}

function containsFewFilters(filterValues: Record<FilterType, string[]>): boolean {
  return (
    Object.values(filterValues).filter((values) => {
      return values.length == 1;
    }).length <= 2
  );
}

function isIncluded(filterValues: string[]): boolean {
  return filterValues.length == 1;
}

function titleToken(filterValues: string[]): string {
  return filterValues[0];
}
